import { forwardRef } from 'react';
import { FormattedDate } from 'react-intl';

import styled from 'styled-components';

import { t } from 'translations';
import { RadioGroup, RadioGroupOption, RadioGroupOptionProps } from 'ui';
import { SmallText } from 'ui/styles/text';

import { BillingPeriod, RatePlan } from '../types';
import { getMonetaryValue } from '../utils/helpers';
import {
  periodToPrepaidTextKey,
  periodToProductNameKey,
  periodToProductPlanNameKey,
} from '../utils/productLocalization';
import { AltProductSelector } from './AltProductSelector';

interface ProductSelectorProps extends Omit<RadioGroupOptionProps, 'labelText'> {
  ratePlans: RatePlan[];
  /** only used in Signup flow */
  isPrepaid?: boolean;
  nextPaymentDate: Date;
  showBadge?: boolean;
  badgeText?: React.ReactNode;
  showNewProductSelector?: boolean;
}

const LabelText = styled.div`
  display: grid;
  gap: 4px;
`;

export const ProductSelector = forwardRef<HTMLInputElement, ProductSelectorProps>(
  (
    {
      ratePlans,
      isPrepaid = false,
      nextPaymentDate,
      showBadge = false,
      badgeText,
      showNewProductSelector = false,
      ...radioProps
    },
    ref,
  ) => {
    if (showNewProductSelector) {
      return (
        <AltProductSelector
          ratePlans={ratePlans}
          isPrepaid={isPrepaid}
          nextPaymentDate={nextPaymentDate}
          {...radioProps}
          ref={ref}
        />
      );
    }

    return (
      <RadioGroup>
        {ratePlans.map(({ billingPeriod, amount, currency }) => {
          const price = getMonetaryValue(amount, currency);

          const priceTextPrepaidActive = t(periodToPrepaidTextKey(billingPeriod), {
            price: price,
          });

          const labelText = (
            <LabelText data-testid={billingPeriod}>
              <SmallText $color="white2" $fontSize="medium" $lineHeight="140%">
                {t(periodToProductPlanNameKey(billingPeriod))}
              </SmallText>

              <>
                <SmallText $color="lightGreen" $fontSize="small" $lineHeight="140%">
                  {isPrepaid
                    ? t('membership_hub_product_page_prepaid_plan_info', {
                        prepaid_validto: <FormattedDate value={nextPaymentDate} format="default" />,
                        prepaid_amount: getMonetaryValue(0, currency),
                      })
                    : t('membership_hub_product_page_plan_info', {
                        date: <FormattedDate value={nextPaymentDate} format="default" />,
                        amount: price,
                      })}
                </SmallText>
                <SmallText $color="grayLight" $fontSize="small" $lineHeight="140%">
                  {priceTextPrepaidActive}
                </SmallText>
              </>
            </LabelText>
          );

          return (
            <RadioGroupOption
              {...radioProps}
              ref={ref}
              key={billingPeriod}
              value={billingPeriod}
              labelText={labelText}
              data-testid={periodToProductNameKey(billingPeriod)}
              showBadge={showBadge && billingPeriod === BillingPeriod.Years}
              badgeText={badgeText}
            />
          );
        })}
      </RadioGroup>
    );
  },
);
